"use client";

import { TABLE, TD, TH } from "@/components/tool-kit/styles";
import { t, tPlural } from "@/lib/i18n/interpolate";
import type { DayNumber } from "@/lib/tool-kit/study/dates";
import {
  type ExamSubject,
  groupDay,
  type StudyDay,
  type SubjectTotals,
} from "@/lib/tool-kit/study/study-schedule";

/*
 * The study schedule generator's two result tables. Every word comes in through
 * `labels`, from the widget's strings.
 */

interface Formatters {
  formatDay: (day: DayNumber) => string;
  fmt: (value: number) => string;
  hoursFor: (sessions: number) => string;
}

function HeaderRow({ labels }: { labels: readonly string[] }) {
  return (
    <thead>
      <tr>
        {labels.map((label) => (
          <th className={`${TH} whitespace-nowrap`} key={label} scope="col">
            {label}
          </th>
        ))}
      </tr>
    </thead>
  );
}

export function SubjectTable({
  subjects,
  totals,
  difficulty,
  head,
  formatDay,
  fmt,
  hoursFor,
}: Formatters & {
  subjects: readonly ExamSubject[];
  totals: readonly SubjectTotals[];
  /** Difficulty names by level: 1, 2, 3. */
  difficulty: Readonly<Record<string, string>>;
  head: readonly string[];
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <HeaderRow labels={head} />
        <tbody>
          {subjects.map((subject, index) => {
            const study = totals[index]?.study ?? 0;
            const review = totals[index]?.review ?? 0;
            return (
              <tr key={`${subject.name}-${subject.exam}`}>
                <td className={TD}>{subject.name}</td>
                <td className={`${TD} whitespace-nowrap`}>
                  {formatDay(subject.exam)}
                </td>
                <td className={TD}>{difficulty[String(subject.difficulty)]}</td>
                <td className={TD}>{fmt(study)}</td>
                <td className={TD}>{fmt(review)}</td>
                <td className={`${TD} whitespace-nowrap`}>
                  {hoursFor(study + review)}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

export interface DayLabels {
  head: readonly string[];
  examBadge: string;
  /** A plural message: "one:{count} session|other:{count} sessions". */
  sessionCount: string;
  review: string;
  dayOff: string;
}

function DayPlan({
  entry,
  subjects,
  labels,
  locale,
  fmt,
}: {
  entry: StudyDay;
  subjects: readonly ExamSubject[];
  labels: DayLabels;
  locale: string;
  fmt: (value: number) => string;
}) {
  return (
    <div className="flex flex-wrap gap-x-3 gap-y-1">
      {entry.exams.map((exam) => (
        <span
          className="rounded bg-primary/10 px-2 py-0.5 font-semibold text-foreground"
          key={`exam-${exam}`}
        >
          {t(labels.examBadge, { subject: subjects[exam].name })}
        </span>
      ))}
      {entry.off ? (
        <span className="text-muted-foreground">{labels.dayOff}</span>
      ) : null}
      {groupDay(entry).map((group) => (
        <span key={group.subject}>
          <strong className="font-medium">
            {subjects[group.subject].name}
          </strong>
          {group.study > 0
            ? ` · ${tPlural(labels.sessionCount, locale, group.study, { count: fmt(group.study) })}`
            : null}
          {group.review > 0 ? (
            <span className="ml-1 rounded border border-border px-1.5 text-muted-foreground text-xs">
              {labels.review}
            </span>
          ) : null}
        </span>
      ))}
    </div>
  );
}

export function DayTable({
  calendar,
  subjects,
  labels,
  locale,
  formatDay,
  fmt,
  hoursFor,
}: Formatters & {
  calendar: readonly StudyDay[];
  subjects: readonly ExamSubject[];
  labels: DayLabels;
  locale: string;
}) {
  return (
    <div className="overflow-x-auto rounded-lg border border-border bg-card">
      <table className={TABLE}>
        <HeaderRow labels={labels.head} />
        <tbody>
          {calendar.map((entry) => (
            <tr className="print:break-inside-avoid" key={entry.day}>
              <td className={`${TD} whitespace-nowrap align-top`}>
                {formatDay(entry.day)}
              </td>
              <td className={`${TD} align-top`}>
                <DayPlan
                  entry={entry}
                  fmt={fmt}
                  labels={labels}
                  locale={locale}
                  subjects={subjects}
                />
              </td>
              <td className={`${TD} whitespace-nowrap align-top`}>
                {entry.sessions.length > 0
                  ? hoursFor(entry.sessions.length)
                  : "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
